import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  Observable,
} from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { shareReplayRefCount, StateService } from '@adreno-common';
import { CallbackComponent } from '../components';
import {
  CallbackSchedulesDto,
  CallbacksDto,
  ListCampaignSchedulesVm,
  ListUserCallbacksVm,
  ScheduledCampaignDto,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class CallbackService extends StateService<State> {
  private _currentCampaignId$ = new BehaviorSubject<number | undefined>(
    undefined
  );
  private _scheduleCampaigns$ = new BehaviorSubject<ScheduledCampaignDto[]>([]);
  private callbackApi = `${environment.apiPath}/callback`;

  campaignSchedules$ = this.select((state) => state.campaignSchedules);
  userCallbacks$ = combineLatest([
    this.select((state) => state.userCallbacks),
    this._scheduleCampaigns$,
    this._currentCampaignId$,
  ]).pipe(
    map(([userCallbacks, scheduledCampaigns, currentCampaignId]) => {
      if (
        scheduledCampaigns
          .find((c) => c.campaignId === currentCampaignId)
          ?.actionType.startsWith('OB')
      ) {
        //OB campaign currently loaded/launched - limit all callbacks to that campaign
        return [userCallbacks, [currentCampaignId]] as const;
      }
      //otherwise show all callbacks for currently scheduled campaigns (IB & OB blended scenarios)
      const now = new Date();
      const onShiftCampaigns =
        scheduledCampaigns
          ?.filter(
            (campaign) =>
              campaign.shifts?.some(
                (shift) => shift.startUtc <= now && now <= shift.finishUtc
              ) || false
          )
          .map((c) => c.campaignId) || [];
      return [userCallbacks, onShiftCampaigns] as const;
    }),
    map(([userCallbacks, onShiftCampaigns]) =>
      userCallbacks?.filter((ucb) => onShiftCampaigns.includes(ucb.campaignId))
    ),
    shareReplayRefCount()
  );
  // eslint-disable-next-line @typescript-eslint/member-ordering
  callbacksDue$ = this.userCallbacks$.pipe(
    map((ucbs) => ucbs?.some((ucb) => ucb.dueNow === 'Yes') || false)
  );

  constructor(private http: HttpClient, private modalSvc: NgbModal) {
    super(initalCallbackState);
  }

  callbackModal$() {
    const modalRef = this.modalSvc.open(CallbackComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    return from(modalRef.result);
  }

  cancelCallback$(recordId: number) {
    return this.http.delete(`${this.callbackApi}/cancel/${recordId}`, {});
  }

  set currentCampaignId(campaignId: number | undefined) {
    this._currentCampaignId$.next(campaignId);
  }

  listCampaignSchedules(campaignId: number, contactutcoffset: number) {
    this.state = { campaignSchedules: undefined };
    this.http
      .get<ListCampaignSchedulesVm>(
        `${this.callbackApi}/schedules/${campaignId}/${contactutcoffset}`
      )
      .subscribe((vm) => {
        this.state = { campaignSchedules: vm.callbackSchedules };
      });
  }

  loadUserCallbacks(): Observable<void> {
    const userutcoffset = 0 - new Date().getTimezoneOffset();
    return this.http
      .get<ListUserCallbacksVm>(`${this.callbackApi}/${userutcoffset}`)
      .pipe(
        tap((vm) => {
          this.state = { userCallbacks: vm.callbacks };
        }),
        map(() => void 0)
      );
  }

  reserveCallback(recordId: number) {
    return this.http
      .put<any>(`${this.callbackApi}/reserve/${recordId}`, null, {
        observe: 'response',
      })
      .pipe(map((resp) => (resp.status == 202 ? true : false)));
  }

  set scheduledCampaigns(
    sceduledCampaignIds: ScheduledCampaignDto[] | undefined
  ) {
    this._scheduleCampaigns$.next(sceduledCampaignIds || []);
  }

  updateCallback(recordId?: number) {
    const otherUserCallbacks = this.state.userCallbacks?.filter(
      (cb) => cb.recordId !== recordId
    );
    this.state = {
      userCallbacks: otherUserCallbacks,
    };
  }
}

interface State {
  campaignSchedules?: CallbackSchedulesDto;
  userCallbacks?: CallbacksDto[];
}

const initalCallbackState: State = {
  campaignSchedules: undefined,
  userCallbacks: undefined,
};
